import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { Card } from "../../UI/Card.tsx";
import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import { classNames } from "../../UI/Utils/classNames.ts";
import {
  CloserDetailDTO,
  CrHistoryPointDTO,
  useGetSchedulerCloserDetail,
} from "../../HubSpot/useGetSchedulerCloserDetail.ts";
import { SchedulerCloserDTO } from "../../HubSpot/useGetSchedulerClosers.ts";
import { StatsFiguresDTO } from "../../HubSpot/useGetSchedulerCloserStats.ts";
import { CloserStatsScreenPath } from "../Stats/closerStatsPath.ts";
import { CapTrendChart, CrTrendChart } from "../Stats/CloserStatsCharts.tsx";
import { StatsKpi } from "../Stats/StatsKpi.tsx";
import {
  capSaturation,
  dataStartNote,
  formatRate,
} from "../Stats/closerStatsFormat.ts";
import { schedulerCloserIssues } from "../closerIssues.ts";
import { CloserCrCard } from "./CloserCrCard.tsx";
import { CloserCrHistoryChart } from "./CloserCrHistoryChart.tsx";
import { CallHeatmap } from "./CallHeatmap.tsx";
import { CloserAvailabilityTrendCard } from "./CloserAvailabilityTrendCard.tsx";
import { tierBadgeClassName, tierLabel } from "./closerTierColors.ts";
import { PeriodSelector } from "../Stats/PeriodSelector.tsx";
import {
  defaultStatsPeriod,
  rangeError,
  StatsRange,
} from "../Stats/statsPeriod.ts";

function formatDay(day: string): string {
  return dayjs(day).format("DD/MM/YYYY");
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
      {children}
    </h2>
  );
}

function closerTags(
  closer: SchedulerCloserDTO,
): { label: string; className: string }[] {
  const tier = tierLabel(closer.tierRank, closer.inTrial);
  return [
    ...(tier ? [{ label: tier, className: tierBadgeClassName(tier) }] : []),
    ...(closer.availabilityToday === "blocked" && closer.block
      ? [
          {
            label: `Bloccato dal ${formatDay(closer.block.from)} al ${formatDay(closer.block.to)}`,
            className: "bg-amber-100 text-amber-700",
          },
        ]
      : []),
    ...(closer.availabilityToday === "not_started" && closer.bookableFrom
      ? [
          {
            label: `Prenotabile dal ${formatDay(closer.bookableFrom)}`,
            className: "bg-amber-100 text-amber-700",
          },
        ]
      : []),
    {
      label:
        closer.acceptsWeb && closer.acceptsSetter
          ? "Landing e setter"
          : closer.acceptsWeb
            ? "Solo landing"
            : "Solo setter",
      className: "bg-slate-100 text-slate-600",
    },
    {
      label:
        closer.capacity === null
          ? "Cap non impostato"
          : `Cap ${closer.capacity}${closer.usesDefaultCapacity ? " (default)" : ""}`,
      className: "bg-slate-100 text-slate-600",
    },
    ...schedulerCloserIssues(closer)
      .filter((issue) => issue !== "Cap non impostato")
      .map((label) => ({ label, className: "bg-red-100 text-red-700" })),
  ];
}

function backfillNote(history: CrHistoryPointDTO[]): string | null {
  const backfilled = history.filter((h) => h.source === "backfill");
  if (backfilled.length === 0) return null;
  const last = backfilled[backfilled.length - 1].day;
  return `Fino al ${formatDay(last)} i valori sono ricostruiti da HubSpot con la regola del job; dopo sono quelli salvati ogni notte dal job CR.`;
}

function noShowRate(figures: StatsFiguresDTO): number | null {
  const outcomes = figures.callsDone + figures.noShow + figures.toReschedule;
  return outcomes === 0
    ? null
    : (figures.noShow + figures.toReschedule) / outcomes;
}

function DetailBody({ detail }: { detail: CloserDetailDTO }) {
  const figures = detail.stats?.totals ?? null;
  const weeks = detail.stats?.weeks ?? [];
  const note = backfillNote(detail.crHistory);
  const email = detail.closer?.email ?? detail.excluded?.email ?? null;

  return (
    <>
      {detail.excluded && (
        <Card>
          <p className="text-sm text-red-700">
            Escluso dallo scheduler: {detail.excluded.reasonLabel}. Non riceve
            call e non ha un CR attuale.
          </p>
        </Card>
      )}

      {detail.closer && <CloserCrCard detail={detail} closer={detail.closer} />}

      <Card>
        <SectionTitle>Andamento del CR</SectionTitle>
        {detail.crHistory.length === 0 ? (
          <p className="text-sm text-slate-400">
            Nessuna fotografia del CR nel periodo: il job notturno le salva ogni
            giorno e recupera da solo gli ultimi 90 giorni.
          </p>
        ) : (
          <>
            <CloserCrHistoryChart
              history={detail.crHistory}
              team={detail.teamCrHistory}
            />
            {note && <p className="text-xs text-slate-400 mt-2">{note}</p>}
          </>
        )}
      </Card>

      {figures && (
        <>
          <Card>
            <SectionTitle>Call ed esiti per settimana</SectionTitle>
            <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(150px,1fr))] mb-3">
              <StatsKpi
                label="Call effettuate"
                value={figures.callsDone}
                sub="nel periodo"
              />
              <StatsKpi
                label="Deal vinti"
                value={figures.wonDeals}
                sub="nel periodo"
              />
              <StatsKpi
                highlight
                label="CR del periodo"
                value={figures.callsDone === 0 ? "—" : formatRate(figures.cr)}
                sub="deal vinti / call effettuate"
              />
              <StatsKpi
                label="No show · Da rifissare"
                value={`${figures.noShow} · ${figures.toReschedule}`}
                sub={`${formatRate(noShowRate(figures))} delle call esitate`}
              />
            </div>
            <CrTrendChart weeks={weeks} />
          </Card>

          <Card>
            <SectionTitle>Carico e cap</SectionTitle>
            <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(150px,1fr))] mb-3">
              <StatsKpi
                label="Saturazione cap"
                value={formatRate(capSaturation(figures))}
                sub={`${figures.callsOnCapacityDays} call su ${figures.capacityTotal} posti`}
              />
              <StatsKpi
                label="Giorni al cap"
                value={figures.daysAtCap}
                sub={`su ${figures.capacityDays} giorni lavorativi`}
              />
              <StatsKpi
                label="Call oltre il cap"
                value={figures.callsOverCap}
                sub="assegnate sopra il cap"
              />
            </div>
            <CapTrendChart weeks={weeks} />
            <h3 className="text-xs font-semibold text-slate-500 mt-4 mb-2">
              Call dello scheduler per giorno e ora
            </h3>
            <CallHeatmap cells={detail.callHeatmap} />
          </Card>

          <Card>
            <SectionTitle>Prenotazioni dello scheduler</SectionTitle>
            <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
              <StatsKpi
                label="Call prenotate"
                value={figures.booked}
                sub={`Landing ${figures.bookedWeb} · Setter ${figures.bookedSetter}`}
              />
              <StatsKpi
                label="Cancellate"
                value={figures.cancelled}
                sub="nel periodo"
              />
              <StatsKpi
                label="In pending"
                value={figures.pending}
                sub="Reschedule pending"
              />
              <StatsKpi
                label="Riprogrammazioni"
                value={figures.reschedules}
                sub="sulle call del periodo"
              />
            </div>
          </Card>
        </>
      )}

      {email && (
        <CloserAvailabilityTrendCard
          email={email}
          range={{ fromDay: detail.fromDay, toDay: detail.toDay }}
        />
      )}
    </>
  );
}

export function CloserDetailScreen() {
  const navigate = useNavigate();
  const { closerId } = useParams<{ closerId: string }>();
  const { getSchedulerCloserDetail, loading } = useGetSchedulerCloserDetail();
  const [period, setPeriod] = useState(defaultStatsPeriod);
  const [detail, setDetail] = useState<CloserDetailDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => {
    if (!closerId) return;
    const range: StatsRange = period.range;
    const invalid = rangeError(range);
    if (invalid) {
      setError(invalid);
      return;
    }
    const current = ++request.current;
    setError(null);
    getSchedulerCloserDetail({ closerId, ...range })
      .then((data) => {
        if (current === request.current) setDetail(data ?? null);
      })
      .catch((err) => {
        console.error("Error fetching closer detail:", err);
        if (current === request.current) {
          setError(
            err?.response?.status === 404
              ? "Closer non trovato tra gli Utenti Closer."
              : "Errore nel recupero della scheda del closer. Riprova.",
          );
          setDetail(null);
        }
      });
  }, [closerId, period, getSchedulerCloserDetail]);

  const name = detail?.closer?.name ?? detail?.excluded?.name ?? "Closer";
  const email = detail?.closer?.email ?? detail?.excluded?.email ?? null;

  const startNote = detail
    ? dataStartNote(period.range.fromDay, detail.dataFromDay)
    : null;

  return (
    <Layout maxWClass="max-w-full">
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-2">
              <button
                onClick={() => navigate(CloserStatsScreenPath)}
                className="text-sm text-primary hover:underline"
              >
                ← Statistiche closer
              </button>
              <h1 className="font-bold text-2xl">{name}</h1>
              {email && <p className="text-sm text-gray-500">{email}</p>}
              {detail?.closer && (
                <div className="flex flex-wrap gap-1">
                  {closerTags(detail.closer).map((tag) => (
                    <span
                      key={tag.label}
                      className={classNames(
                        "text-xs px-2 py-0.5 rounded",
                        tag.className,
                      )}
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <PeriodSelector period={period} onChange={setPeriod} />
          </div>
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
          {startNote && (
            <p className="mt-3 text-xs text-gray-500">{startNote}</p>
          )}
        </Card>

        {loading && !detail && <LoadingBlock sizeClassName="h-64 w-full" />}
        {detail && <DetailBody detail={detail} />}
      </div>
    </Layout>
  );
}
